// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo, useState } from 'react';
import {
  ActionableNotification,
  Checkbox,
  InlineNotification,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
  TextInput,
} from '@carbon/react';
import {
  emailDomain,
  InvitePartialFailureError,
  InviteRefusedError,
  useAssignableRoles,
  useFederatedDomains,
  useInviteUser,
} from '../../shared/users';
import type {
  AccountMode,
  AssignableRole,
  AssignableScope,
  InviteRefusal,
  InviteUserResult,
} from '../../shared/users';
import { useSharedSession } from '../../shared/auth/useSharedSession';
import { useRegions } from '../../shared/provisioning';
import { roleLabel } from './orgRole';
import styles from './InviteUserModal.module.css';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  /** The user was added and every role grant succeeded — the caller surfaces
   *  this however it likes, e.g. a toast with the mode's sign-in copy. */
  onSuccess: (result: InviteUserResult, name: string) => void;
  /** The user exists but couldn't be granted all of the chosen access (ADO
   *  #31303) — the modal still closes, and the caller links to their profile
   *  where the grant can be finished or retried. */
  onPartialFailure: (userId: string, message: string) => void;
  /** Open an existing user's profile, when the email already belongs to them. */
  onOpenUser: (userId: string) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SCOPE_LABEL: Record<AssignableScope, string> = {
  ORGANIZATION: 'Organization',
  REGION: 'Region',
  SITE: 'Site',
};

const REFUSAL_MESSAGE: Record<InviteRefusal, string> = {
  existing_local: 'A user with this email already exists.',
  duplicate_local: 'A user with this email already exists.',
  domain_not_federated:
    "This email domain doesn't use company sign-in. Add them as a local account instead.",
  organization_mismatch: 'This email domain is not part of your organization.',
};

interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  /** Set when the admin overrides the mode chosen from the email domain. */
  modeOverride: AccountMode | null;
  roleId: string;
  scope: AssignableScope | null;
  resourceIds: string[];
}

const EMPTY_FORM: FormState = {
  email: '',
  firstName: '',
  lastName: '',
  modeOverride: null,
  roleId: '',
  scope: null,
  resourceIds: [],
};

/** The narrowest scope a role allows: least privilege unless the admin widens it. */
function defaultScope(role: AssignableRole): AssignableScope {
  return role.scopes[role.scopes.length - 1];
}

/** Admin Center's label for the four canonical roles, IAM's name for any other.
 *  Two IAM roles that map to one label are told apart by their IAM name. */
function labelRoles(roles: AssignableRole[]): Map<string, string> {
  const base = roles.map(r => (r.canonicalRole ? roleLabel(r.canonicalRole) : r.roleName));
  return new Map(
    roles.map((r, i) => {
      const clash = base.indexOf(base[i]) !== base.lastIndexOf(base[i]);
      return [r.roleId, clash && r.canonicalRole ? `${base[i]} (${r.roleName})` : base[i]];
    })
  );
}

/**
 * Add user (AB#35252, AB#35260): adds a person as a federated employee (signs
 * in with their company account) or a local account, and grants one role from
 * the organization's full catalog at the scope the admin picks. The mode
 * defaults from the email domain and the admin may switch it either way; for
 * company sign-in, IAM has the final say on whether the domain qualifies.
 */
export default function InviteUserModal({
  open,
  onClose,
  onSuccess,
  onPartialFailure,
  onOpenUser,
}: InviteUserModalProps) {
  const { sites, organizationId } = useSharedSession();
  const { roles, isLoading: isRolesLoading, isError: isRolesError } = useAssignableRoles();
  const { domains, isLoading: isDomainsLoading, isError: isDomainsError } = useFederatedDomains();
  const regionsQuery = useRegions(organizationId ?? undefined);
  const { invite, isInviting } = useInviteUser();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<{
    firstName?: boolean;
    lastName?: boolean;
    email?: boolean;
  }>({});
  const [submitError, setSubmitError] = useState<{
    message: string;
    existingUserId?: string;
  } | null>(null);

  const roleLabels = useMemo(() => labelRoles(roles), [roles]);
  const regions = regionsQuery.data ?? [];

  const email = form.email.trim();
  const isEmailValid = EMAIL_PATTERN.test(email);
  const domain = isEmailValid ? emailDomain(email) : null;
  // The list holds only this organization's company sign-in domains, so an
  // unlisted domain may be unmapped or belong to another organization — the
  // client can't tell which. It defaults to local, but company sign-in stays
  // available and IAM decides (EMAIL_DOMAIN_NOT_FEDERATED vs
  // ORGANIZATION_MISMATCH).
  const isListedDomain = domain !== null && domains.includes(domain);
  const mode: AccountMode = form.modeOverride ?? (isListedDomain ? 'federated' : 'local');
  const namesRequired = mode === 'local';

  const selectedRole = roles.find(r => r.roleId === form.roleId) ?? null;
  const scope = selectedRole ? (form.scope ?? defaultScope(selectedRole)) : null;
  const needsResources = scope === 'REGION' || scope === 'SITE';
  const resourceOptions =
    scope === 'REGION'
      ? regions.map(r => ({ id: r.regionId, label: r.label, hint: '' }))
      : sites.map(s => ({ id: s.id, label: s.label, hint: s.region }));

  const isNameValid =
    !namesRequired || (form.firstName.trim().length > 0 && form.lastName.trim().length > 0);
  const canSubmit =
    isEmailValid &&
    isNameValid &&
    selectedRole !== null &&
    (!needsResources || form.resourceIds.length > 0) &&
    !isInviting &&
    !isDomainsLoading;

  function resetAndClose() {
    setForm(EMPTY_FORM);
    setTouched({});
    setSubmitError(null);
    onClose();
  }

  function toggleResource(id: string) {
    setForm(f => ({
      ...f,
      resourceIds: f.resourceIds.includes(id)
        ? f.resourceIds.filter(r => r !== id)
        : [...f.resourceIds, id],
    }));
  }

  async function handleSubmit() {
    if (!canSubmit || !selectedRole || !scope) {
      // Enter also reaches the primary action — show the required-field
      // feedback rather than submitting silently.
      setTouched({ firstName: true, lastName: true, email: true });
      return;
    }
    setSubmitError(null);
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    try {
      const result = await invite({
        mode,
        email,
        firstName,
        lastName,
        roleId: selectedRole.roleId,
        roleName: roleLabels.get(selectedRole.roleId) ?? selectedRole.roleName,
        scope,
        resourceIds: needsResources ? form.resourceIds : [],
      });
      onSuccess(result, `${firstName} ${lastName}`.trim() || email);
      resetAndClose();
    } catch (err) {
      if (err instanceof InvitePartialFailureError) {
        onPartialFailure(err.userId, err.message);
        resetAndClose();
        return;
      }
      // Refused or failed outright: nothing was created, so keep what the
      // admin typed and let them correct it.
      if (err instanceof InviteRefusedError) {
        setSubmitError({
          message: REFUSAL_MESSAGE[err.reason],
          existingUserId: err.existingUserId,
        });
        return;
      }
      setSubmitError({
        message: err instanceof Error ? err.message : 'The user could not be added.',
      });
    }
  }

  return (
    <Modal
      open={open}
      className={styles.modal}
      modalHeading="Add user"
      primaryButtonText="Add user"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!canSubmit}
      // The only ways out are Cancel and Add user — no close "X", Escape, or
      // click-outside, so an admin can't lose a half-filled form by
      // fat-fingering a click near the dialog. `onRequestClose` is Carbon's
      // combined handler for all three, so it's a no-op here and Cancel gets
      // its own handler; the "X" itself is hidden via CSS.
      onSecondarySubmit={resetAndClose}
      onRequestClose={() => {}}
      onRequestSubmit={handleSubmit}
      size="sm"
    >
      {isRolesError && (
        <InlineNotification
          kind="error"
          title="Unable to load the role catalog."
          subtitle="A role can't be granted until this loads — close and reopen this dialog to retry."
          hideCloseButton
          lowContrast
          className={styles.notification}
        />
      )}
      {!isRolesLoading && !isRolesError && roles.length === 0 && (
        <InlineNotification
          kind="warning"
          title="No assignable roles found."
          subtitle="This organization's role catalog has no roles that can be granted from here, so a user can't be added."
          hideCloseButton
          lowContrast
          className={styles.notification}
        />
      )}
      {isDomainsError && (
        <InlineNotification
          kind="warning"
          title="Couldn't check which email domains use company sign-in."
          subtitle="Pick the sign-in type yourself; company sign-in is checked when you add the user."
          hideCloseButton
          lowContrast
          className={styles.notification}
        />
      )}
      {submitError &&
        (submitError.existingUserId ? (
          <ActionableNotification
            kind="error"
            title={submitError.message}
            hideCloseButton
            lowContrast
            inline
            className={styles.notification}
            actionButtonLabel="Open their profile"
            onActionButtonClick={() => {
              const userId = submitError.existingUserId as string;
              resetAndClose();
              onOpenUser(userId);
            }}
          />
        ) : (
          <InlineNotification
            kind="error"
            title={submitError.message}
            hideCloseButton
            lowContrast
            className={styles.notification}
          />
        ))}

      <div className={styles.field}>
        <TextInput
          id="invite-email"
          type="email"
          labelText="Email"
          placeholder="name@company.com"
          value={form.email}
          invalid={touched.email && !isEmailValid}
          invalidText="Enter a valid email address."
          onChange={e => setForm(f => ({ ...f, email: e.target.value, modeOverride: null }))}
          onBlur={() => setTouched(t => ({ ...t, email: true }))}
        />
      </div>

      <div className={styles.field}>
        <RadioButtonGroup
          legendText="Sign-in"
          name="invite-mode"
          orientation="vertical"
          valueSelected={mode}
          onChange={value => setForm(f => ({ ...f, modeOverride: value as AccountMode }))}
        >
          <RadioButton id="invite-mode-federated" value="federated" labelText="Company sign-in" />
          <RadioButton id="invite-mode-local" value="local" labelText="Local account" />
        </RadioButtonGroup>
        {domain && !isListedDomain && !isDomainsLoading && !isDomainsError && (
          <p className={styles.sitesNote}>
            @{domain} isn&apos;t one of your organization&apos;s company sign-in domains.
          </p>
        )}
      </div>

      <div className={`${styles.field} ${styles.nameRow}`}>
        <TextInput
          id="invite-first-name"
          labelText={namesRequired ? 'First name' : 'First name (optional)'}
          placeholder="e.g. Dana"
          value={form.firstName}
          invalid={namesRequired && touched.firstName && form.firstName.trim().length === 0}
          invalidText="Enter a first name."
          onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
          onBlur={() => setTouched(t => ({ ...t, firstName: true }))}
        />
        <TextInput
          id="invite-last-name"
          labelText={namesRequired ? 'Last name' : 'Last name (optional)'}
          placeholder="e.g. Whitfield"
          value={form.lastName}
          invalid={namesRequired && touched.lastName && form.lastName.trim().length === 0}
          invalidText="Enter a last name."
          onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
          onBlur={() => setTouched(t => ({ ...t, lastName: true }))}
        />
      </div>

      <div className={styles.field}>
        {/* Every role IAM's catalog can grant at an organization, region or
            site (AB#35260) — IAM decides on submit whether this admin may
            grant it. No default: the admin picks the access deliberately. */}
        <Select
          id="invite-role"
          labelText="Role"
          value={form.roleId}
          disabled={roles.length === 0}
          onChange={e =>
            setForm(f => ({ ...f, roleId: e.target.value, scope: null, resourceIds: [] }))
          }
        >
          <SelectItem value="" text="Choose a role" disabled hidden />
          {roles.map(role => (
            <SelectItem
              key={role.roleId}
              value={role.roleId}
              text={roleLabels.get(role.roleId) ?? role.roleName}
            />
          ))}
        </Select>
      </div>

      {selectedRole && scope && (
        <div className={styles.field}>
          {selectedRole.scopes.length > 1 && (
            <div className={styles.field}>
              <RadioButtonGroup
                legendText="Grant at"
                name="invite-scope"
                valueSelected={scope}
                onChange={value =>
                  setForm(f => ({ ...f, scope: value as AssignableScope, resourceIds: [] }))
                }
              >
                {selectedRole.scopes.map(s => (
                  <RadioButton
                    key={s}
                    id={`invite-scope-${s}`}
                    value={s}
                    labelText={SCOPE_LABEL[s]}
                  />
                ))}
              </RadioButtonGroup>
            </div>
          )}

          {needsResources ? (
            <>
              <div className={styles.sitesHeader}>
                <span className={styles.sitesLabel}>
                  {scope === 'REGION' ? 'Regions' : 'Sites'}
                </span>
                <span
                  className={`${styles.sitesHint} ${form.resourceIds.length > 0 ? '' : styles.sitesHintInvalid}`}
                >
                  {form.resourceIds.length > 0
                    ? `${form.resourceIds.length} of ${resourceOptions.length} selected`
                    : 'none yet — pick at least one'}
                </span>
              </div>
              <div className={styles.siteList}>
                {resourceOptions.map(option => (
                  <div key={option.id} className={styles.siteRow}>
                    <Checkbox
                      id={`invite-resource-${option.id}`}
                      labelText={
                        <span className={styles.siteLabel}>
                          {option.label}
                          {option.hint && <span className={styles.siteRegion}>{option.hint}</span>}
                        </span>
                      }
                      checked={form.resourceIds.includes(option.id)}
                      onChange={() => toggleResource(option.id)}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <span className={styles.sitesLabel}>Access</span>
              <p className={styles.sitesNote}>
                Granted across the whole organization — every region and site.
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
